import { Icon } from '@tabler/icons-react';
import NSIcon, { NSIconSize } from 'bricks/NSIcon/NSIcon';
import React, { MouseEventHandler, useState } from 'react';
import { Button, Tooltip } from 'reactstrap';
import classNames from 'classnames';
import useAuthContext from 'contexts/AuthContext';
import { AccessLevels } from 'types/auth';
import { BUTTON_DISABLED_BY_AUTH_TOOLTIP, userClasses } from 'constants/auth';
import handleIconKeyDown from 'helpers/handleIconKeyDown';
import RenderIf from '../../components/RenderIf/RenderIf';
import { subprojectLifeStages, NSBUTTON_SIZES } from '../constants';

type IconPosition = 'left' | 'right';

interface INSButtonBaseProps {
    id?: string;
    color?: string;
    text?: string;
    icon?: Icon;
    iconPosition?: IconPosition;
    size?: string;
    disabled?: boolean;
    disabledTooltip?: string | React.JSX.Element; // must provide a unique button `id` to show
    tooltipAutohide?: boolean;
    tooltip?: string | React.JSX.Element;
    className?: string;
    innerContentClassName?: string;
    includeTooltip?: boolean;
    allowReadOnlyAccess?: boolean;
    subprojectLifeStage?: number;
    outline?: boolean;
    buttonType?: string;
    iconSize?: NSIconSize | undefined;
    children?: React.ReactNode;
    formId?: string;
    onMouseDown?: MouseEventHandler<HTMLButtonElement>;
    isLoading?: boolean;
    dataTestId?: string;
    ariaLabel?: string;
    ariaPressed?: boolean;
    featureId?: number;
}

// Icon-only buttons invoke the callback with no event argument, so that variant only admits zero-arg callbacks.
export type INSButtonProps = INSButtonBaseProps & (
    | { iconOnly: true; callback?: VoidFunction }
    | { iconOnly?: false; callback?: React.MouseEventHandler<HTMLButtonElement> | VoidFunction }
);

export const NSButtonBase = ({
    id = '',
    color = '',
    text = '',
    icon,
    iconPosition = 'left',
    size,
    disabled = false,
    disabledTooltip = '',
    tooltipAutohide = true,
    callback = undefined,
    tooltip = '',
    className = '',
    innerContentClassName,
    includeTooltip = true,
    allowReadOnlyAccess = false,
    subprojectLifeStage = subprojectLifeStages.ACTIVE,
    iconOnly = false,
    outline = false,
    buttonType = 'button',
    iconSize = 'sm',
    children = null,
    formId,
    onMouseDown = () => {},
    isLoading = false,
    dataTestId,
    ariaLabel,
    ariaPressed,
    featureId,
    ...rest
}: INSButtonProps) => {
    const [hover, setHover] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    const toggleHover = () => setHover(!hover);

    const toggleTooltip = () => setShowTooltip(!showTooltip);

    const btnColor = color || '';
    const btnText = text || '';
    let btnTitle = '';
    const btnIcon = icon || null;
    const btnIconSize = iconSize || 'sm';
    const btnId = id || '';
    let btnTooltip: string | React.JSX.Element | null = tooltip || null;

    const loadingSpinner = () => (
        <span
            data-testid="loading-spinner"
            className={classNames('spinner-border', {
                'spinner-border-sm': size !== NSBUTTON_SIZES.LARGE,
            })}
            style={size === NSBUTTON_SIZES.LARGE ? { height: '25px', width: '25px' } : {}}
        />
    );

    const iconOnlyCallback = () => {
        if (callback && !disabled && !isLoading) {
            (callback as VoidFunction)();
        }
    };

    if (disabled) {
        btnTitle = (btnTooltip as string) || '';
        btnTooltip = disabledTooltip || '';
    }

    return (
        <>
            <RenderIf isTrue={!iconOnly}>
                {/* wrapper workaround for showing tooltips when button is disabled. */}
                <span id={`${btnId}-tooltip-wrapper`} className="tooltip-wrapper" data-testid="tooltip-wrapper">
                    <Button
                        id={id}
                        onMouseEnter={toggleHover}
                        onMouseLeave={toggleHover}
                        onMouseDown={onMouseDown}
                        color={btnColor}
                        outline={outline}
                        disabled={disabled || isLoading}
                        size={size}
                        onClick={callback}
                        title={btnTitle}
                        className={classNames('NSButton rounded', className, {
                            'cursor--disabled': disabled || isLoading,
                        })}
                        type={buttonType as 'button' | 'reset' | 'submit' | undefined}
                        data-testid={dataTestId}
                        aria-label={ariaLabel}
                        aria-pressed={ariaPressed}
                        {...(formId && { form: formId })}
                        {...rest}
                    >
                        <span className={innerContentClassName}>
                            <RenderIf isTrue={isLoading}>{loadingSpinner()}</RenderIf>

                            <RenderIf isTrue={!isLoading}>
                                <RenderIf isTrue={btnIcon && iconPosition === 'left'}>
                                    <NSIcon className={`icon ${btnText ? 'mr-1' : ''}`} icon={btnIcon as Icon} size={btnIconSize} />
                                </RenderIf>

                                {children || btnText}

                                <RenderIf isTrue={btnIcon && iconPosition === 'right'}>
                                    <NSIcon className={`icon ${btnText ? 'ml-1' : ''}`} icon={btnIcon as Icon} size={btnIconSize} />
                                </RenderIf>
                            </RenderIf>
                        </span>
                    </Button>
                </span>

                <RenderIf isTrue={btnId && btnTooltip && includeTooltip}>
                    <Tooltip target={`${btnId}-tooltip-wrapper`} isOpen={showTooltip} toggle={toggleTooltip} autohide={tooltipAutohide}>
                        {btnTooltip}
                    </Tooltip>
                </RenderIf>
            </RenderIf>

            <RenderIf isTrue={iconOnly}>
                <NSIcon
                    className={className}
                    icon={btnIcon as Icon}
                    size={btnIconSize}
                    id={btnId}
                    onClick={iconOnlyCallback}
                    onKeyDown={handleIconKeyDown(iconOnlyCallback)}
                    data-testid={dataTestId}
                    aria-label={ariaLabel}
                    aria-hidden={false}
                    role="button"
                    tabIndex={callback && !disabled && !isLoading ? 0 : undefined}
                    {...rest}
                />

                <RenderIf isTrue={btnTooltip && btnId && includeTooltip}>
                    <Tooltip target={btnId as string | HTMLElement | React.RefObject<HTMLElement>} isOpen={showTooltip} toggle={toggleTooltip}>
                        {btnTooltip}
                    </Tooltip>
                </RenderIf>
            </RenderIf>
        </>
    );
};

// This is a wrapper for the NSButtonBase component that adds authentication checks
export const NSButton = ({
    disabled = false,
    disabledTooltip = '',
    allowReadOnlyAccess = false,
    featureId,
    isLoading,
    ...props
}: INSButtonProps) => {
    const {
        isFeatureAccessibleToUser,
        isLoadingAuthData,
        role,
        user,
    } = useAuthContext();

    let btnIsDisabled = disabled;
    let currentDisabledTooltip = disabledTooltip || '';

    if (featureId) {
        const requiredAccessLevel = allowReadOnlyAccess
            ? AccessLevels.READ_ACCESS
            : AccessLevels.READ_WRITE_ACCESS;

        const hasAccess = isFeatureAccessibleToUser([featureId], requiredAccessLevel);
        if (!hasAccess) {
            btnIsDisabled = true;
            currentDisabledTooltip = BUTTON_DISABLED_BY_AUTH_TOOLTIP;
        }
    } else if (!allowReadOnlyAccess && role?.isReadOnly && user?.userClass !== userClasses.CLASS_SUPER_ADMIN) {
        btnIsDisabled = true;
        currentDisabledTooltip = BUTTON_DISABLED_BY_AUTH_TOOLTIP;
    }

    return (
        <NSButtonBase
            {...props}
            disabled={btnIsDisabled}
            disabledTooltip={currentDisabledTooltip}
            isLoading={isLoading || isLoadingAuthData}
        />
    );
};

export default NSButton;
